// ============================================================================
// useVoiceRoomMedia.ts — медиа голосовой комнаты (LiveKit, ТОЛЬКО звук).
//
// Состояние/присутствие ведёт СЕРВЕР (VoiceRoomService); медиа — LiveKit:
// пропуск выдаёт GetRoomToken (комната voice_<roomId>, identity = пользователь).
// Если LiveKit не настроен — сервер отвечает FailedPrecondition: присутствие и
// список участников продолжают работать (честное состояние «медиа не настроены»).
// Удалённый звук — собственные <audio> в контейнере (в core livekit-client
// авто-плеера нет); при отказе автоплея показывается кнопка «Включить звук».
// ============================================================================

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  type RemoteAudioTrack,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import { errorCode, friendlyError, logErrorToJournal, logSwallowed } from '@/lib/errors';
import { createLogger } from '@/lib/logger';
import { getSDK } from '@/store/sdkStore';

const log = createLogger('VoiceRooms.Media');

// ConnectRPC: FailedPrecondition (число в connect-es / имя в connect-go).
const CODE_FAILED_PRECONDITION = 9;

function isFailedPrecondition(e: unknown): boolean {
  const code = errorCode(e);
  if (typeof code === 'number') return code === CODE_FAILED_PRECONDITION;
  if (typeof code === 'string') {
    return code.trim().toLowerCase().replace(/[\s_-]/g, '') === 'failedprecondition';
  }
  return false;
}

export type VoiceMediaStatus = 'idle' | 'connecting' | 'connected' | 'unavailable' | 'error';

export interface VoicePeer {
  identity: string;
  name: string;
  speaking: boolean;
}

export interface VoiceMediaState {
  status: VoiceMediaStatus;
  error: string;
  peers: VoicePeer[];
  localSpeaking: boolean;
  needAudioTap: boolean;
  startAudio: () => Promise<void>;
  setMicEnabled: (on: boolean) => Promise<void>;
  retry: () => void;
}

/**
 * Подключение к медиа комнаты (аудио).
 * `enabled` — пользователь В КОМНАТЕ (state=active); выключение/размонтирование
 * отключает LiveKit. `initialMuted` применяется в момент подключения микрофона.
 */
export function useVoiceRoomMedia(
  roomId: string,
  enabled: boolean,
  initialMuted: boolean,
  audioContainerRef: RefObject<HTMLDivElement | null>,
): VoiceMediaState {
  const [status, setStatus] = useState<VoiceMediaStatus>('idle');
  const [error, setError] = useState('');
  const [peers, setPeers] = useState<VoicePeer[]>([]);
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const [needAudioTap, setNeedAudioTap] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const roomRef = useRef<Room | null>(null);
  const audiosRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const mutedRef = useRef(initialMuted);
  mutedRef.current = initialMuted;

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled || !roomId) {
      setStatus('idle');
      setPeers([]);
      return;
    }
    let cancelled = false;
    const audios = audiosRef.current;
    audios.clear();
    setStatus('connecting');
    setError('');
    setNeedAudioTap(false);

    const refreshPeers = (room: Room): void => {
      const speaking = new Set(room.activeSpeakers.map((p) => p.identity));
      const list: VoicePeer[] = [];
      room.remoteParticipants.forEach((p: RemoteParticipant) => {
        list.push({
          identity: p.identity,
          name: p.name && p.name.trim() ? p.name : p.identity,
          speaking: speaking.has(p.identity),
        });
      });
      list.sort((a, b) => a.name.localeCompare(b.name));
      setPeers(list);
      setLocalSpeaking(room.localParticipant.isSpeaking);
    };

    const attachAudio = (track: RemoteAudioTrack, participant: RemoteParticipant): void => {
      let el = audios.get(participant.identity);
      if (!el) {
        el = document.createElement('audio');
        el.autoplay = true;
        el.setAttribute('playsinline', 'true');
        el.style.display = 'none';
        audioContainerRef.current?.appendChild(el);
        audios.set(participant.identity, el);
      }
      track.attach(el);
      void el.play().catch((e) => {
        logSwallowed(e, 'Голосовые комнаты: автоплей звука участника');
        setNeedAudioTap(true);
      });
    };

    void (async () => {
      try {
        const token = await getSDK().voiceRooms.getRoomToken({ roomId });
        if (cancelled) return;
        if (!token.token || !token.url) {
          throw new Error('Сервер не выдал пропуск голосовой комнаты');
        }
        const room = new Room();
        roomRef.current = room;
        room
          .on(RoomEvent.ParticipantConnected, () => refreshPeers(room))
          .on(RoomEvent.ParticipantDisconnected, () => refreshPeers(room))
          .on(RoomEvent.TrackSubscribed, (
            track: RemoteTrack,
            _pub: RemoteTrackPublication,
            participant: RemoteParticipant,
          ) => {
            if (track.kind === Track.Kind.Audio) {
              attachAudio(track as RemoteAudioTrack, participant);
            }
            refreshPeers(room);
          })
          .on(RoomEvent.TrackUnsubscribed, () => refreshPeers(room))
          .on(RoomEvent.TrackMuted, () => refreshPeers(room))
          .on(RoomEvent.TrackUnmuted, () => refreshPeers(room))
          .on(RoomEvent.ActiveSpeakersChanged, () => refreshPeers(room))
          .on(RoomEvent.AudioPlaybackStatusChanged, () => setNeedAudioTap(!room.canPlaybackAudio));

        await room.connect(token.url, token.token);
        if (cancelled) {
          await room.disconnect();
          return;
        }
        try {
          await room.startAudio();
        } catch (e) {
          logSwallowed(e, 'Голосовые комнаты: запуск воспроизведения звука');
        }
        setNeedAudioTap(!room.canPlaybackAudio);
        try {
          await room.localParticipant.setMicrophoneEnabled(!mutedRef.current);
        } catch (e) {
          log.error('микрофон голосовой комнаты недоступен', { error: e });
        }
        setStatus('connected');
        refreshPeers(room);
      } catch (e) {
        if (cancelled) return;
        if (isFailedPrecondition(e)) {
          setStatus('unavailable');
          setError('Медиа не настроены — присутствие и список участников работают');
        } else {
          log.error('voice room media connect', { error: e });
          logErrorToJournal(e, 'Голосовая комната: подключение к медиа');
          setStatus('error');
          setError(friendlyError(e));
        }
      }
    })();

    return () => {
      cancelled = true;
      const room = roomRef.current;
      roomRef.current = null;
      audios.forEach((el) => el.remove());
      audios.clear();
      if (room) {
        room.removeAllListeners();
        void room.disconnect().catch((e) => {
          logSwallowed(e, 'Голосовые комнаты: отключение медиа');
        });
      }
    };
  }, [roomId, enabled, attempt, audioContainerRef]);

  const startAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    await room.startAudio();
    setNeedAudioTap(!room.canPlaybackAudio);
    for (const el of Array.from(audiosRef.current.values())) {
      void el.play().catch((e) => {
        logSwallowed(e, 'Голосовые комнаты: повторное включение звука');
      });
    }
  }, []);

  const setMicEnabled = useCallback(async (on: boolean) => {
    const room = roomRef.current;
    if (!room) {
      throw new Error('Медиа ещё не подключены');
    }
    await room.localParticipant.setMicrophoneEnabled(on);
  }, []);

  return {
    status,
    error,
    peers,
    localSpeaking,
    needAudioTap,
    startAudio,
    setMicEnabled,
    retry,
  };
}
